import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config/env';
import { fetchJson } from '../utils/api';
import { validateAuthState, getPostLoginRedirect } from '../utils/auth';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const refreshSession = useCallback(async () => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const data = await fetchJson(config.AUTH_ME_URL, {
        credentials: 'include',
      });
      if (data?.isAuthenticated && data?.user) {
        setUser({ id: data.user.id, name: data.user.name, email: data.user.email });
        setIsAuthenticated(true);
      } else if (data?.id) {
        setUser({ id: data.id, name: data.name, email: data.email });
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const login = useCallback((currentPath) => {
    const state = (() => {
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    })();
    sessionStorage.setItem('auth_state', state);
    sessionStorage.setItem('postLoginRedirect', currentPath || '/');

    const returnUrl = `${config.DOMAIN}${config.AUTH_CALLBACK_PATH}`;
    const url = new URL(config.LOGIN_REDIRECT_URL);
    url.searchParams.set('returnUrl', returnUrl);
    url.searchParams.set('state', state);
    url.searchParams.set('source', 'learnovative');

    window.location.href = url.toString();
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetchJson(config.LOGOUT_URL, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // swallow logout endpoint errors
    }
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/';
  }, []);

  const exchangeCode = useCallback(async (code, returnedState) => {
    if (!validateAuthState(returnedState)) {
      throw new Error('INVALID_STATE');
    }

    setIsLoading(true);
    setAuthError(null);

    try {
      const redirectUri = `${config.DOMAIN}${config.AUTH_CALLBACK_PATH}`;
      const data = await fetchJson(config.EXCHANGE_CODE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, state: returnedState, redirectUri }),
      });

      if (data && data.user) {
        setUser({ id: data.user.id, name: data.user.name, email: data.user.email });
        setIsAuthenticated(true);
      } else if (data && data.id) {
        setUser({ id: data.id, name: data.name, email: data.email });
        setIsAuthenticated(true);
      }

      return getPostLoginRedirect();
    } catch (error) {
      console.error('[AuthContext] Code exchange failed:', {
        message: error?.message,
        status: error?.status,
        code: error?.code,
        payload: error?.payload,
      });
      setAuthError(error.message || 'Authentication failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        authError,
        login,
        logout,
        refreshSession,
        exchangeCode,
        clearAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
